import { useCallback } from 'react';
import type { VoidFunctionComponent } from 'react';

import LabeledNumberInput from '../../generic/Forms/LabeledNumberInput';
import { LabeledToggle } from '../../generic/Forms/LabeledToggle';
import ReadOnlySection from '../../generic/ReadOnlySection';
import Select from '../../generic/Select/Select';
import type { DependencyOutdatedVersionPart } from '../../../../shared/model-typings';
import type { SelectOption } from '../../../types';

import type { OutdatedRule } from './types';

import styles from './OutdatedRulesComponent.module.less';

type Props = {
  outdatedRule: OutdatedRule;
  onOutdatedRuleChange: (rule: OutdatedRule) => void;
  outdatedFallbackRule: OutdatedRule;
  onOutdatedFallbackRuleChange: (rule: OutdatedRule) => void;
};

const versionSelectOptions: Array<SelectOption<DependencyOutdatedVersionPart>> = [
  { value: 'MAJOR', label: 'Major' },
  { value: 'MINOR', label: 'Minor' },
  { value: 'PATCH', label: 'Patch' },
];

const OutdatedRulesComponent: VoidFunctionComponent<Props> = ({
  outdatedRule,
  onOutdatedRuleChange,
  outdatedFallbackRule,
  onOutdatedFallbackRuleChange,
}) => {
  const setVersionPart = useCallback(({ value }: { value: DependencyOutdatedVersionPart }) => {
    onOutdatedRuleChange({
      ...outdatedRule,
      dependencyOutdatedVersionPart: value,
    });
  }, [outdatedRule, onOutdatedRuleChange]);

  const setVersionDiff = useCallback((value: string) => {
    const numValue = Number(value);
    if (value === '' || Number.isNaN(numValue) || numValue < 1) {
      return;
    }
    onOutdatedRuleChange({
      ...outdatedRule,
      dependencyOutdatedVersionDiff: numValue,
    });
  }, [outdatedRule, onOutdatedRuleChange]);

  const onToggleOutdated = useCallback((isChecked: boolean) => {
    onOutdatedRuleChange({
      ...outdatedRule,
      enabled: isChecked,
    });
  }, [outdatedRule, onOutdatedRuleChange]);

  const setVersionDiffFallback = useCallback((value: string) => {
    const numValue = Number(value);
    if (value === '' || Number.isNaN(numValue) || numValue < 1) {
      return;
    }
    onOutdatedFallbackRuleChange({
      ...outdatedFallbackRule,
      dependencyOutdatedVersionDiff: numValue,
    });
  }, [outdatedFallbackRule, onOutdatedFallbackRuleChange]);

  const onToggleOutdatedFallback = useCallback((isChecked: boolean) => {
    onOutdatedFallbackRuleChange({
      ...outdatedFallbackRule,
      enabled: isChecked,
    });
  }, [outdatedFallbackRule, onOutdatedFallbackRuleChange]);

  return (
    <div>
      <div className={styles.ruleRow}>
        <LabeledToggle
          label="Semver Outdated Rule"
          isChecked={outdatedRule.enabled ?? false}
          onChange={onToggleOutdated}
        />
        <ReadOnlySection isReadOnly={!outdatedRule.enabled} className={styles.ruleInputs}>
          <span>Flag dependency when</span>
          <div className={styles.selectWrapper}>
            <Select
              id="dependencyOutdatedVersionPartSelect"
              aria-label="Version part"
              options={versionSelectOptions}
              value={versionSelectOptions.find((opt) => opt.value === outdatedRule.dependencyOutdatedVersionPart)}
              isDisabled={!outdatedRule.enabled}
              onChange={(e) => {
                if (e) {
                  setVersionPart(e);
                }
              }}
            />
          </div>
          <span>version is</span>
          <div className={styles.numberInputWrapper}>
            <LabeledNumberInput
              label="Versions behind latest"
              min={1}
              size="small"
              isDisabled={!outdatedRule.enabled}
              value={outdatedRule.dependencyOutdatedVersionDiff ?? 1}
              onChange={setVersionDiff}
            />
          </div>
          <span>versions behind latest</span>
        </ReadOnlySection>
      </div>

      <div className={styles.ruleRow}>
        <LabeledToggle
          label="Fallback Outdated Rule"
          isChecked={outdatedFallbackRule.enabled ?? false}
          onChange={onToggleOutdatedFallback}
        />
        <ReadOnlySection isReadOnly={!outdatedFallbackRule.enabled} className={styles.ruleInputs}>
          <span>Fallback when version is</span>
          <div className={styles.numberInputWrapper}>
            <LabeledNumberInput
              label="Versions behind latest"
              min={1}
              size="small"
              isDisabled={!outdatedFallbackRule.enabled}
              value={outdatedFallbackRule.dependencyOutdatedVersionDiff ?? 1}
              onChange={setVersionDiffFallback}
            />
          </div>
          <span>versions behind latest</span>
        </ReadOnlySection>
      </div>
    </div>
  );
};

export default OutdatedRulesComponent;
